import { Component, ReactNode } from "react";
import { CustomAlert } from "./Alert/CustomAlert";

/**
 * Rede de segurança do carregamento sob demanda.
 *
 * `lazy` que falha ao buscar o arquivo LANÇA durante o render, e o host não tem nenhum error
 * boundary — sem esta classe, o React desmonta a aplicação inteira e o usuário fica com a tela em
 * branco, não só sem o módulo.
 *
 * E o cenário é real, não teórico: durante um rollout o ambiente serve duas versões ao mesmo tempo
 * (aconteceu em homologação em 12/08), então quem está com a página aberta pede um arquivo cujo
 * nome a versão nova não tem mais. Aqui isso vira um aviso com o caminho de saída — recarregar.
 *
 * GEN-3799 — `oQue` diz o que não carregou: na modal de certidões o aviso é sobre a ficha, não
 * sobre o módulo inteiro.
 *
 * GEN-2959 — saiu de `pages/BaseCadastral/baseCadastralHost` para cá: com as páginas em chunks lazy
 * (PERF-05) o AppShell também precisa dele, e importá-lo de lá puxaria o host da Base Cadastral para
 * o bundle inicial.
 */
export class ChunkErrorBoundary extends Component<
  // GEN-4063 — `aoFalhar` avisa quem hospeda: um resumo fora do boundary não fica em "Carregando…" para sempre.
  { children: ReactNode; oQue?: string; aoFalhar?: () => void },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  // O aviso fala de atualização do sistema (a causa comum de chunk que não carrega), mas o boundary pega
  // QUALQUER erro de render — sem este log, um bug de tela viraria "recarregue a página" sem rastro.
  componentDidCatch(error: unknown) {
    console.error("[ChunkErrorBoundary]", this.props.oQue ?? "a Base Cadastral", error);
    this.props.aoFalhar?.();
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <CustomAlert
        status="error"
        descriptionHtml={`<b>Não foi possível carregar ${
          this.props.oQue ?? "a Base Cadastral"
        }.</b> Isso costuma acontecer logo depois de uma atualização do sistema — recarregue a página para pegar a versão nova.`}
        icon
        variant="alert"
        type="three"
        className="alert-instance"
        isClosable={false}
      />
    );
  }
}
